import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Divider, Stack, Tab, Typography } from '@mui/material'
import { TablePagination } from 'components/TablePagination'
import PageWrapper from 'hoc/PageWrapper'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryState } from 'react-router-use-location-state'
import { CommitteeEvaluationItem } from './committee-evaluation-item'
import { Loading } from 'components/Loading'
import { Error } from 'components/Error'
import { useCommitteeEvaluationOverviewQuery } from 'services/committee-evaluation'
import { ECommitteeEvaluationStatus } from 'types/committee-evaluation'

export const ProcurementCommittee = () => {
  const [tab, setTab] = useState<ECommitteeEvaluationStatus>(
    ECommitteeEvaluationStatus.PENDING
  )
  const { t } = useTranslation()
  const [page, setPage] = useQueryState('page', 0)

  const rowsPerPage = 10

  const args = {
    page,
    size: rowsPerPage,
    status: tab as ECommitteeEvaluationStatus,
  } as const

  const {
    data: committeeEvaluations,
    isError,
    isLoading,
  } = useCommitteeEvaluationOverviewQuery(args, { refetchOnMountOrArgChange: true })

  const tenderTabs = useMemo(() => {
    return [
      {
        value: ECommitteeEvaluationStatus.PENDING,
        label: t('documents-tab'),
      },
      {
        value: ECommitteeEvaluationStatus.CONFIRMED,
        label: t('confirmed-documents-tab'),
      },
      {
        value: ECommitteeEvaluationStatus.REJECTED,
        label: t('rejected-documents-tab'),
      },
    ]
  }, [t])

  if (isLoading) return <Loading fullPage={false} />
  if (isError) return <Error fullPage={false} />

  return (
    <PageWrapper>
      <Stack spacing={2}>
        <TabContext value={tab}>
          <TabList
            onChange={(_event, newValue) => {
              setTab(newValue)
              setPage(0)
            }}
            sx={{
              '&& .MuiTabs-flexContainer': {
                justifyContent: 'center',
              },
            }}
          >
            {tenderTabs.map(({ value, label }) => (
              <Tab
                sx={{ background: '#FFF' }}
                key={value}
                label={label}
                value={value}
              />
            ))}
          </TabList>
          <Divider />
          <Typography variant="h6">
            {tab === ECommitteeEvaluationStatus.PENDING
              ? t('documents-tab')
              : tab === ECommitteeEvaluationStatus.CONFIRMED
                ? t('confirmed-documents-tab')
                : t('rejected-documents-tab')}
          </Typography>
          {committeeEvaluations && (
            <>
              <TabPanel value={ECommitteeEvaluationStatus.PENDING}>
                <CommitteeEvaluationItem
                  committeeEvaluations={committeeEvaluations?.items}
                  status={ECommitteeEvaluationStatus.PENDING}
                />
              </TabPanel>
              <TabPanel value={ECommitteeEvaluationStatus.CONFIRMED}>
                <CommitteeEvaluationItem
                  committeeEvaluations={committeeEvaluations?.items}
                  status={ECommitteeEvaluationStatus.CONFIRMED}
                />
              </TabPanel>
              <TabPanel value={ECommitteeEvaluationStatus.REJECTED}>
                <CommitteeEvaluationItem
                  committeeEvaluations={committeeEvaluations?.items}
                  status={ECommitteeEvaluationStatus.REJECTED}
                />
              </TabPanel>
            </>
          )}
        </TabContext>
        {committeeEvaluations && committeeEvaluations.info.totalRows !== 0 && (
          <TablePagination
            total={committeeEvaluations.info.totalRows}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={setPage}
          />
        )}
      </Stack>
    </PageWrapper>
  )
}
